import axios from 'axios';
import { API_URL } from '../config';

/**
 * Upload temporary attachments to a created order or offer
 * @param {number} id - The ID of the created order or offer
 * @param {Array} tempAttachments - Array of temporary attachment objects
 * @param {Function} getToken - Function to get authentication token
 * @param {Function} onProgress - Optional progress callback
 * @param {string} type - Type of attachment: 'order' or 'offer' (default: 'order')
 * @returns {Promise<boolean>} - Success status
 */
export const uploadTempAttachments = async (id, tempAttachments, getToken, onProgress, type = 'order') => {
  if (!tempAttachments || tempAttachments.length === 0) {
    return true; // No attachments to upload
  }

  try {
    const formData = new FormData();
    
    // Add all files to form data
    tempAttachments.forEach((attachment, index) => {
      if (attachment.file) {
        formData.append('attachments', attachment.file);
      }
    });

    const token = await getToken();
    
    // Determine the endpoint based on type
    const endpoint = type === 'offer' 
      ? `${API_URL}/offer-attachments/${id}`
      : `${API_URL}/order-attachments/${id}`;
    
    const response = await axios.post(
      endpoint,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        }
      }
    );

    // Clean up temporary preview URLs
    tempAttachments.forEach(attachment => {
      if (attachment.preview) {
        URL.revokeObjectURL(attachment.preview);
      }
    });

    return response.data.success;
  } catch (error) {
    console.error('Error uploading attachments:', error);
    
    // Clean up temporary preview URLs even on error
    tempAttachments.forEach(attachment => {
      if (attachment.preview) {
        URL.revokeObjectURL(attachment.preview);
      }
    });
    
    throw error;
  }
};

/**
 * Get count and total size of temporary attachments
 * @param {Array} tempAttachments - Array of temporary attachment objects
 * @returns {Object} - Statistics about attachments
 */
export const getAttachmentStats = (tempAttachments) => {
  if (!tempAttachments || tempAttachments.length === 0) {
    return { count: 0, totalSize: 0, totalSizeMB: 0 };
  }

  const totalSize = tempAttachments.reduce((sum, attachment) => {
    return sum + (attachment.fileSize || 0);
  }, 0);

  return {
    count: tempAttachments.length,
    totalSize,
    totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2)
  };
};